import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Heading,
  VStack,
  HStack,
  Text,
  Button,
  Grid,
  GridItem,
  Card,
  CardBody,
  Badge,
  Icon,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Image,
  Spinner,
  Alert,
  AlertIcon,
  useToast,
  useBreakpointValue,
  Link as ChakraLink
} from '@chakra-ui/react';
import { 
  FaSearch, 
  FaFlask, 
  FaVial, 
  FaClock, 
  FaDollarSign,
  FaBars,
  FaTimes
} from 'react-icons/fa';
import { Link as RouterLink } from 'react-router-dom';
import { supabase } from '../supabaseClient';

const LabTestDirectory = () => {
  const { shortSlug } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  
  // State
  const [lab, setLab] = useState(null);
  const [tests, setTests] = useState([]);
  const [filteredTests, setFilteredTests] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const isMobile = useBreakpointValue({ base: true, md: false });

  // Fetch lab and its offered tests
  useEffect(() => {
    fetchLabData();
  }, [shortSlug]);

  const fetchLabData = async () => {
    try {
      // First, get the lab by short_slug
      const { data: labData, error: labError } = await supabase
        .from('labs')
        .select('*')
        .eq('short_slug', shortSlug)
        .single();

      if (labError) throw labError;
      if (!labData) {
        toast({
          title: 'Lab not found',
          description: 'The requested lab directory does not exist.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        navigate('/');
        return;
      }

      setLab(labData);

      // Fetch tests offered by this lab
      const [testOfferingsResult, panelOfferingsResult] = await Promise.all([
        supabase
          .from('lab_test_offerings')
          .select(`
            *,
            tests (*)
          `)
          .eq('lab_id', labData.id)
          .eq('is_offered', true),
        supabase
          .from('lab_panel_offerings')
          .select(`
            *,
            panels (*)
          `)
          .eq('lab_id', labData.id)
          .eq('is_offered', true)
      ]);

      if (testOfferingsResult.error) throw testOfferingsResult.error;
      if (panelOfferingsResult.error) throw panelOfferingsResult.error;

      // Combine tests and panels
      const combinedTests = [
        ...(testOfferingsResult.data || []).map(offering => ({
          ...offering.tests,
          type: 'test',
          lab_price: offering.lab_cash_price,
          lab_code: offering.lab_test_code
        })),
        ...(panelOfferingsResult.data || []).map(offering => ({
          ...offering.panels,
          type: 'panel',
          lab_price: offering.lab_cash_price,
          lab_code: offering.lab_test_code
        }))
      ];

      setTests(combinedTests);
      setFilteredTests(combinedTests);

    } catch (error) {
      console.error('Error fetching lab data:', error);
      toast({
        title: 'Error loading lab directory',
        description: 'Please try again later',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Search functionality
  useEffect(() => {
    if (!searchTerm) {
      setFilteredTests(tests);
    } else {
      const filtered = tests.filter(test =>
        test.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        test.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        test.lab_code?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredTests(filtered);
    }
  }, [searchTerm, tests]);

  const handleTestSelect = (test) => {
    navigate(`/lab/${shortSlug}/test/${test.id}`);
  };

  if (loading) {
    return (
      <Box minH="100vh" bg="gray.50">
        <Container maxW="1200px" py={8}>
          <VStack spacing={4}>
            <Spinner size="xl" color="blue.500" />
            <Text>Loading lab directory...</Text>
          </VStack>
        </Container>
      </Box>
    );
  }

  if (!lab) {
    return (
      <Box minH="100vh" bg="gray.50">
        <Container maxW="1200px" py={8}>
          <Alert status="error">
            <AlertIcon />
            Lab directory not found
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Lab Header */}
      <Box bg="white" shadow="sm" borderBottom="1px" borderColor="gray.200">
        <Container maxW="1200px">
          <Flex 
            h={16} 
            alignItems="center" 
            justifyContent="space-between"
            px={{ base: 4, md: 0 }}
          >
            {/* Lab Logo and Name */}
            <HStack spacing={4}>
              {lab.logo_url && (
                <Image 
                  src={lab.logo_url} 
                  alt={`${lab.name} logo`}
                  h={10}
                  objectFit="contain"
                />
              )}
              <VStack align="start" spacing={0}>
                <Text fontSize="lg" fontWeight="bold" color="gray.800">
                  {lab.name}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Test Directory
                </Text>
              </VStack>
            </HStack>

            {/* Mobile Menu Toggle */}
            {isMobile && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <Icon as={mobileMenuOpen ? FaTimes : FaBars} />
              </Button>
            )}

            {/* Desktop Navigation */}
            {!isMobile && (
              <HStack spacing={4}>
                <ChakraLink as={RouterLink} to="/" color="blue.600" fontWeight="medium">
                  Home
                </ChakraLink>
                <ChakraLink as={RouterLink} to="/test-directory" color="blue.600" fontWeight="medium">
                  All Tests
                </ChakraLink>
              </HStack>
            )}
          </Flex>

          {/* Mobile Menu */}
          {mobileMenuOpen && isMobile && (
            <Box pb={4} borderTop="1px" borderColor="gray.200">
              <VStack spacing={2} align="stretch">
                <ChakraLink as={RouterLink} to="/" py={2} px={4}>
                  Home
                </ChakraLink>
                <ChakraLink as={RouterLink} to="/test-directory" py={2} px={4}>
                  All Tests
                </ChakraLink>
              </VStack>
            </Box>
          )}
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxW="1200px" py={8}>
        <VStack spacing={8} align="stretch">
          
          {/* Title and Search */}
          <VStack spacing={6} align="center" textAlign="center">
            <VStack spacing={2}>
              <Heading size="2xl" color="gray.800">
                {lab.name} Test Directory
              </Heading>
              <Text fontSize="lg" color="gray.600" maxW="600px">
                Browse our comprehensive catalog of laboratory tests and panels available through {lab.name}.
              </Text>
            </VStack>

            {/* Search Bar */}
            <Box w="100%" maxW="500px">
              <InputGroup size="lg">
                <InputLeftElement pointerEvents="none">
                  <Icon as={FaSearch} color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Search tests by name or code..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  bg="white"
                  border="2px"
                  borderColor="gray.200"
                  _hover={{ borderColor: "blue.300" }}
                  _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)" }}
                />
              </InputGroup>
            </Box>

            {/* Results Count */}
            <Text color="gray.600">
              {filteredTests.length} test{filteredTests.length !== 1 ? 's' : ''} available
            </Text>
          </VStack>

          {/* Tests Grid */}
          {filteredTests.length === 0 ? (
            <Alert status="info">
              <AlertIcon />
              {searchTerm ? 'No tests found matching your search.' : 'No tests are currently offered by this lab.'}
            </Alert>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
              {filteredTests.map((test) => (
                <Card 
                  key={test.id} 
                  cursor="pointer"
                  onClick={() => handleTestSelect(test)}
                  _hover={{ 
                    transform: 'translateY(-2px)', 
                    shadow: 'lg',
                    borderColor: 'blue.300'
                  }}
                  transition="all 0.2s"
                  border="1px"
                  borderColor="gray.200"
                  bg="white"
                >
                  <CardBody p={6}>
                    <VStack align="start" spacing={4}>
                      
                      {/* Test Header */}
                      <HStack justify="space-between" w="100%">
                        <Badge
                          colorScheme={test.type === 'panel' ? 'purple' : 'blue'}
                          px={2}
                          py={1}
                          borderRadius="md"
                          fontSize="xs"
                          fontWeight="medium"
                        >
                          <Icon 
                            as={test.type === 'panel' ? FaFlask : FaVial} 
                            mr={1} 
                          />
                          {test.type === 'panel' ? 'Panel' : 'Test'}
                        </Badge>
                        
                        {(test.lab_price || test.cash_price) && (
                          <Badge colorScheme="green" variant="subtle">
                            <Icon as={FaDollarSign} mr={1} />
                            ${test.lab_price || test.cash_price}
                          </Badge>
                        )}
                      </HStack>

                      {/* Test Name */}
                      <VStack align="start" spacing={2} flex={1}>
                        <Heading size="md" color="gray.800" lineHeight="short">
                          {test.name}
                        </Heading>
                        
                        {test.lab_code && (
                          <Text fontSize="sm" color="gray.500" fontFamily="mono">
                            Lab Code: {test.lab_code}
                          </Text>
                        )}
                        
                        {test.description && (
                          <Text fontSize="sm" color="gray.600" noOfLines={3}>
                            {test.description}
                          </Text>
                        )}
                      </VStack>

                      {/* Test Details */}
                      <HStack spacing={4} pt={2}>
                        {test.fasting_required && (
                          <HStack spacing={1}>
                            <Icon as={FaClock} color="orange.500" size="sm" />
                            <Text fontSize="xs" color="orange.600">
                              Fasting Required
                            </Text>
                          </HStack>
                        )}
                      </HStack>

                    </VStack>
                  </CardBody>
                </Card>
              ))}
            </SimpleGrid>
          )}

        </VStack>
      </Container>
    </Box>
  );
};

export default LabTestDirectory; 